import { TaskType } from "@/shared/domain/taskTypes";
/**
 * Centralized logic for detecting label/entity/relation mismatches between:
 * - Dataset (train set)
 * - Benchmarks
 * - External Model Results (manual model predictions)
 *
 * Used in multiple flows (Data Collection "Next" gate, Training "Let's train" gate).
 */
function toKeySet(countsObj) {
  if (!countsObj || typeof countsObj !== 'object') return new Set();
  return new Set(Object.keys(countsObj).filter(Boolean));
}

function diffKeys(sourceSet, baseSet) {
  return [...sourceSet].filter((k) => !baseSet.has(k));
}

/**
 * @param {object} params
 * @param {string} params.taskType - usecase task type string
 * @param {object} params.datasetCounts - {label_counts, entity_counts, relation_counts}
 * @param {object|null} [params.benchmarkCounts] - same shape as datasetCounts
 * @param {object|null} [params.externalCounts] - same shape as datasetCounts (manual model predictions)
 * @returns {string[]} array of human-readable mismatch messages
 */
export function getLabelTypeMismatchMessages({
  taskType,
  datasetCounts,
  benchmarkCounts = null,
  externalCounts = null,
}) {
  const messages = [];

  const datasetLabels = toKeySet(datasetCounts?.label_counts);
  const datasetEntities = toKeySet(datasetCounts?.entity_counts);
  const datasetRelations = toKeySet(datasetCounts?.relation_counts);

  const addBenchmarkMessages = () => {
    if (!benchmarkCounts) return;

    const benchmarkLabels = toKeySet(benchmarkCounts?.label_counts);
    const benchmarkEntities = toKeySet(benchmarkCounts?.entity_counts);
    const benchmarkRelations = toKeySet(benchmarkCounts?.relation_counts);

    if (taskType === TaskType.classification) {
      const missing = diffKeys(benchmarkLabels, datasetLabels);
      if (missing.length > 0) {
        messages.push(
          `Benchmark contains ${missing.length} label${missing.length === 1 ? '' : 's'} not present in the dataset`
        );
      }
      return;
    }

    if (taskType === TaskType.NER) {
      const missing = diffKeys(benchmarkEntities, datasetEntities);
      if (missing.length > 0) {
        messages.push(
          `Benchmark contains ${missing.length} entit${missing.length === 1 ? 'y' : 'ies'} not present in the dataset`
        );
      }
      return;
    }

    if (taskType === TaskType.NER_WITH_RELATIONS) {
      const missingEntities = diffKeys(benchmarkEntities, datasetEntities);
      const missingRelations = diffKeys(benchmarkRelations, datasetRelations);
      if (missingEntities.length > 0) {
        messages.push(
          `Benchmark contains ${missingEntities.length} entit${missingEntities.length === 1 ? 'y' : 'ies'} not present in the dataset`
        );
      }
      if (missingRelations.length > 0) {
        messages.push(
          `Benchmark contains ${missingRelations.length} relation${missingRelations.length === 1 ? '' : 's'} not present in the dataset`
        );
      }
    }
  };

  const addExternalMessages = () => {
    if (!externalCounts) return;

    const externalLabels = toKeySet(externalCounts?.label_counts);
    const externalEntities = toKeySet(externalCounts?.entity_counts);
    const externalRelations = toKeySet(externalCounts?.relation_counts);

    if (taskType === TaskType.classification) {
      const missing = diffKeys(externalLabels, datasetLabels);
      if (missing.length > 0) {
        messages.push(
          `External Model Results contains ${missing.length} label${missing.length === 1 ? '' : 's'} not present in the dataset`
        );
      }
      return;
    }

    if (taskType === TaskType.NER) {
      const missing = diffKeys(externalEntities, datasetEntities);
      if (missing.length > 0) {
        messages.push(
          `External Model Results contains ${missing.length} entit${missing.length === 1 ? 'y' : 'ies'} not present in the dataset`
        );
      }
      return;
    }

    if (taskType === TaskType.NER_WITH_RELATIONS) {
      const missingEntities = diffKeys(externalEntities, datasetEntities);
      const missingRelations = diffKeys(externalRelations, datasetRelations);
      if (missingEntities.length > 0) {
        messages.push(
          `External Model Results contains ${missingEntities.length} entit${missingEntities.length === 1 ? 'y' : 'ies'} not present in the dataset`
        );
      }
      if (missingRelations.length > 0) {
        messages.push(
          `External Model Results contains ${missingRelations.length} relation${missingRelations.length === 1 ? '' : 's'} not present in the dataset`
        );
      }
    }
  };

  addBenchmarkMessages();
  addExternalMessages();

  // De-dupe
  return [...new Set(messages)];
}

/**
 * Build error-shaped insights for the Data Overview when train and benchmark
 * disagree on labels / entities / relations. Mirrors the copy used by
 * `getLabelTypeMismatchMessages` for the benchmark-only case; when the train
 * set has outputs missing from the benchmark (or both directions), a single
 * combined row is used with a detailed tooltip.
 *
 * @param {object} params
 * @param {string} params.taskType
 * @param {object} params.datasetCounts - from `get-label-counts` (train)
 * @param {object} params.benchmarkCounts - from `get-label-counts` (benchmark)
 * @returns {Array<{ type: 'error', content: string, explanation: string }>}
 */
export function getTrainBenchmarkCrossOutputInsights({
  taskType,
  datasetCounts,
  benchmarkCounts,
}) {
  if (!datasetCounts || !benchmarkCounts) return [];

  const buildOne = (benchmarkOnly, trainOnly, { noun, nouns, unmatchedWord }) => {
    if (!benchmarkOnly.length && !trainOnly.length) return null;

    if (benchmarkOnly.length && trainOnly.length) {
      return {
        type: "error",
        content: `Train and benchmark ${nouns} do not match`,
        explanation: [
          `Benchmark-only ${nouns}: ${benchmarkOnly.join(", ")}.`,
          `Train-only ${nouns}: ${trainOnly.join(", ")}.`,
        ].join(" "),
      };
    }

    if (benchmarkOnly.length) {
      const n = benchmarkOnly.length;
      return {
        type: "error",
        content: `Benchmark contains ${n} ${n === 1 ? noun : nouns} not present in the dataset`,
        explanation: `Unmatched ${unmatchedWord}: ${benchmarkOnly.join(", ")}.`,
      };
    }

    const n = trainOnly.length;
    return {
      type: "error",
      content: `Dataset contains ${n} ${n === 1 ? noun : nouns} not present in the benchmark`,
      explanation: `Unmatched ${unmatchedWord}: ${trainOnly.join(", ")}.`,
    };
  };

  if (taskType === TaskType.classification) {
    const train = toKeySet(datasetCounts?.label_counts);
    const bench = toKeySet(benchmarkCounts?.label_counts);
    const insight = buildOne(diffKeys(bench, train), diffKeys(train, bench), {
      noun: "label",
      nouns: "labels",
      unmatchedWord: "labels",
    });
    return insight ? [insight] : [];
  }

  if (taskType === TaskType.NER) {
    const train = toKeySet(datasetCounts?.entity_counts);
    const bench = toKeySet(benchmarkCounts?.entity_counts);
    const insight = buildOne(diffKeys(bench, train), diffKeys(train, bench), {
      noun: "entity",
      nouns: "entities",
      unmatchedWord: "entities",
    });
    return insight ? [insight] : [];
  }

  if (taskType === TaskType.NER_WITH_RELATIONS) {
    const out = [];

    const trainE = toKeySet(datasetCounts?.entity_counts);
    const benchE = toKeySet(benchmarkCounts?.entity_counts);
    const entityInsight = buildOne(diffKeys(benchE, trainE), diffKeys(trainE, benchE), {
      noun: "entity",
      nouns: "entities",
      unmatchedWord: "entities",
    });
    if (entityInsight) out.push(entityInsight);

    const trainR = toKeySet(datasetCounts?.relation_counts);
    const benchR = toKeySet(benchmarkCounts?.relation_counts);
    const relationInsight = buildOne(diffKeys(benchR, trainR), diffKeys(trainR, benchR), {
      noun: "relation",
      nouns: "relations",
      unmatchedWord: "relations",
    });
    if (relationInsight) out.push(relationInsight);

    return out;
  }

  return [];
}

